import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Input,
  LightModeSemanticColors,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import {
  TABLE_BODY_CELL_STYLE,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import { KOMA_COUNT } from '~/constants/koma'
import {
  type GridCellUpdate,
  useExcelGridEditing,
} from '~/hooks/useExcelGridEditing'
import {
  type CellPos,
  isInRange,
  isMultiCellRange,
  parseTsv,
  stepCell,
} from '~/models/gridSelection'
import {
  KOMA_DIVIDER_BORDER,
  isKomaIndexDividerAfter,
} from '~/models/komaDivider'
import { parseSlotValue } from '~/models/splitSupplySlot'
import { planSlotPaste } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import {
  slotEndLabel,
  slotStartLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/slotTime'

const NS =
  'page:adminOcctoMaster.splitSupplyContract.basePattern.modal' as const
const TIME_COL = 0
const VALUE_COL = 1
const BOUNDS = { rowCount: KOMA_COUNT, colCount: 2 }

type Props = {
  /** 48 個 (入力途中の文字列のまま)。 */
  values: readonly string[]
  onChange: (next: string[]) => void
  invalidIndex?: number
}

const rangeLabel = (row: number): string =>
  `${slotStartLabel(row)}〜${slotEndLabel(row)}`

/**
 * 48 コマの値の表 (Excel 風)。 セルのクリック選択・ドラッグ範囲選択・矢印キー移動
 * (Shift で範囲拡張)・F2 / Enter / 文字入力で編集開始・Enter で確定して下へ・Esc で取消・
 * Tab で右へ (行末は次の行の先頭へ折り返す)・Ctrl+C で選んだ範囲だけを TSV コピー
 * (見出しは付けない)・Ctrl+V で選択セルから貼り付け (縦 48 / 横 48 とも。 数値にならない
 * セルは反映せず件数を通知する)・Delete で選択範囲を空にする・Ctrl+Z / Ctrl+Y で元に戻す /
 * やり直す (この表を開いている間の編集だけ)。 見出しは「ただのセル」として
 * 選択・コピーの対象にする。 値の検査は OK 時 (`validatePatternDraft`) が行う。
 */
export const SlotValueGrid = ({
  values,
  onChange,
  invalidIndex,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 履歴は表の中の編集だけを持つ。 モーダルを閉じて表が外れれば消え、 保存前の編集にだけ効く
  const undoStack = useRef<string[][]>([])
  const redoStack = useRef<string[][]>([])
  const [pasteSkippedCount, setPasteSkippedCount] = useState(0)
  const grid = useExcelGridEditing({
    bounds: BOUNDS,
    initialCell: { row: 0, col: VALUE_COL },
    isValueCell: (_row, col) => col === VALUE_COL,
    valueAt: (row) => values[row] ?? '',
    cellText: (row, col) =>
      col === TIME_COL ? rangeLabel(row) : (values[row] ?? ''),
    onCellsChange: (updates) => {
      setPasteSkippedCount(0)
      const next = [...values]
      for (const { row, value } of updates) next[row] = value
      // 値が変わらない操作は履歴に積まない (元に戻しても何も起きない手数を作らない)
      if (next.every((v, i) => v === values[i])) return
      undoStack.current.push(values.slice())
      redoStack.current = []
      onChange(next)
    },
  })
  const { rootRef, range, focus, editing } = grid

  const undo = (): void => {
    const prev = undoStack.current.pop()
    if (prev === undefined) return
    setPasteSkippedCount(0)
    redoStack.current.push(values.slice())
    onChange(prev)
  }

  const redo = (): void => {
    const next = redoStack.current.pop()
    if (next === undefined) return
    setPasteSkippedCount(0)
    undoStack.current.push(values.slice())
    onChange(next)
  }

  // Tab の移動先 (右へ、 行末は次の行の先頭へ。 Shift は逆向き)。 表の端では null
  const tabTarget = (pos: CellPos, backward: boolean): CellPos | null => {
    const dir = backward ? -1 : 1
    const sameRow = stepCell(pos, 0, dir, BOUNDS)
    if (sameRow !== null) return sameRow
    return stepCell(
      { row: pos.row, col: backward ? BOUNDS.colCount : -1 },
      dir,
      dir,
      BOUNDS,
    )
  }

  const moveTo = (pos: CellPos): void => {
    grid.setAnchor(pos)
    grid.setFocus(pos)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    if (e.ctrlKey || e.metaKey) {
      const key = e.key.toLowerCase()
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault()
        redo()
      }
      return
    }
    if (e.key === 'Tab') {
      // 表の端では移動先が無いので、 キーボードの罠にならないようブラウザの Tab に任せる
      const next = tabTarget(focus, e.shiftKey)
      if (next === null) return
      e.preventDefault()
      moveTo(next)
      return
    }
    grid.handleKeyDown(e)
  }

  // 編集中の Tab は確定して次のセルへ。 表のルートへ focus を戻すと input の blur で確定される
  const editInputHandlers = (
    row: number,
  ): ReturnType<typeof grid.editInputHandlers> => {
    const base = grid.editInputHandlers(row, VALUE_COL)
    return {
      ...base,
      onKeyDown: (e) => {
        if (
          e.key === 'Tab' &&
          !e.nativeEvent.isComposing &&
          e.keyCode !== 229
        ) {
          e.preventDefault()
          e.stopPropagation()
          rootRef.current?.focus()
          moveTo(
            tabTarget({ row, col: VALUE_COL }, e.shiftKey) ?? {
              row,
              col: VALUE_COL,
            },
          )
          return
        }
        base.onKeyDown?.(e)
      },
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    e.preventDefault()
    const text = e.clipboardData.getData('text')
    const matrix = parseTsv(text)
    const single = matrix.length === 1 && matrix[0]?.length === 1
    if (single && isMultiCellRange(range)) {
      // 1 つの値は選んだ範囲の値セルすべてへ (数値にならない値は反映しない)
      const updates: GridCellUpdate[] = []
      const value = matrix[0]?.[0] ?? ''
      const rowCount = range.rowMax - range.rowMin + 1
      if (parseSlotValue(value) !== undefined) {
        for (let row = range.rowMin; row <= range.rowMax; row++) {
          updates.push({ row, col: VALUE_COL, value })
        }
      }
      grid.setCells(updates)
      setPasteSkippedCount(updates.length === 0 ? rowCount : 0)
      return
    }
    const { cells, skippedCount } = planSlotPaste(text, range.rowMin)
    grid.setCells(
      cells.map(({ index, value }) => ({ row: index, col: VALUE_COL, value })),
    )
    setPasteSkippedCount(skippedCount)
    if (cells.length > 0) {
      const last = cells[cells.length - 1]?.index ?? range.rowMin
      grid.setAnchor({ row: range.rowMin, col: VALUE_COL })
      grid.setFocus({ row: last, col: VALUE_COL })
    }
  }

  return (
    <>
      {pasteSkippedCount > 0 && (
        <Box marginBottom={2}>
          <CompactAlert
            role="alert"
            status="warning"
            title={t(`${NS}.pasteSkipped`, { count: pasteSkippedCount })}
          />
        </Box>
      )}
      <Box
        ref={rootRef}
        overflowY="auto"
        maxHeight="320px"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
        _focusVisible={{ outline: '2px solid', outlineColor: 'Focus.clarity' }}
        aria-label={t(`${NS}.gridAria`)}
        onCopy={grid.handleCopy}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        role="grid"
        tabIndex={0}
      >
        <table
          style={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr role="row">
              <th style={{ ...TABLE_HEADER_CELL_STYLE, width: '50%' }}>
                {t(`${NS}.slotColumn`)}
              </th>
              <th
                style={{
                  ...TABLE_HEADER_CELL_STYLE,
                  width: '50%',
                  textAlign: 'right',
                }}
              >
                {t(`${NS}.valueColumn`)}
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: KOMA_COUNT }, (_, row) => (
              <tr key={row} role="row">
                {[TIME_COL, VALUE_COL].map((col) => {
                  const selected = isInRange(range, row, col)
                  const isFocus = focus.row === row && focus.col === col
                  const isEditing = editing?.row === row && col === VALUE_COL
                  return (
                    <td
                      key={col}
                      aria-invalid={
                        col === VALUE_COL && row === invalidIndex
                          ? true
                          : undefined
                      }
                      aria-label={
                        col === VALUE_COL
                          ? t(`${NS}.slotValueAria`, { range: rangeLabel(row) })
                          : undefined
                      }
                      aria-selected={selected}
                      data-focused={isFocus || undefined}
                      data-koma-divider-after={
                        isKomaIndexDividerAfter(row) || undefined
                      }
                      onDoubleClick={() => {
                        if (col === VALUE_COL) grid.startEdit(row, col)
                      }}
                      {...grid.cellMouseHandlers(row, col)}
                      role="gridcell"
                      style={{
                        ...TABLE_BODY_CELL_STYLE,
                        ...(isKomaIndexDividerAfter(row)
                          ? { borderBottom: KOMA_DIVIDER_BORDER }
                          : {}),
                        lineHeight: '20px',
                        ...(col === VALUE_COL
                          ? {
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                            }
                          : {}),
                        cursor: 'cell',
                        userSelect: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        backgroundColor: selected
                          ? LightModeSemanticColors.Surface
                              .accentPrimaryLightMid.value
                          : undefined,
                        outline:
                          col === VALUE_COL && row === invalidIndex
                            ? `2px solid ${LightModeSemanticColors.Border.negativeMid.value}`
                            : isFocus
                              ? `2px solid ${LightModeSemanticColors.Surface.accentPrimary.value}`
                              : undefined,
                        outlineOffset: -2,
                      }}
                    >
                      {isEditing ? (
                        <Input
                          ref={(el) => {
                            el?.focus()
                          }}
                          textAlign="right"
                          aria-label={t(`${NS}.slotValueEditAria`, {
                            range: rangeLabel(row),
                          })}
                          inputMode="numeric"
                          {...editInputHandlers(row)}
                          size="sm"
                          value={editing?.draft ?? ''}
                        />
                      ) : col === VALUE_COL ? (
                        (values[row] ?? '')
                      ) : (
                        rangeLabel(row)
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </>
  )
}
